import { Component, NgZone, ChangeDetectionStrategy, computed } from '@angular/core'
import { Router } from '@angular/router'

import { App, URLOpenListenerEvent, AppPlugin } from '@capacitor/app'
import { Platform, IonicModule } from '@ionic/angular'
import { SplashScreen } from '@capacitor/splash-screen'

import { ConfigService } from 'src/app/services/config.service'
import { environment } from 'src/environments/environment'
import { SwManagerService } from './services/sw-manager.service'
import { PrinterRecoveryComponent } from './components/printer-recovery/printer-recovery.component'
import { ThermalPrinterService } from './services/thermal-printer.service'

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [IonicModule, PrinterRecoveryComponent]
  })
export class AppComponent {
  /**
   * Single source for the recovery dialog. Bluetooth and USB recoveries belong to different printers
   * and never run together, so one dialog serves both and two backdrops can never stack.
   */
  readonly objPrinterRecovery = computed<{ intPercent: number, strMessageKey: string } | null>(() => {
    const objBluetooth = this.thermalPrinter.objBluetoothRecovery()
    if (objBluetooth) {
      return { intPercent: objBluetooth.intPercent, strMessageKey: 'ThermalPrinterService.reconnectingBluetooth' }
    }

    const objUsb = this.thermalPrinter.objUsbRecovery()
    if (objUsb) {
      return { intPercent: objUsb.intPercent, strMessageKey: 'ThermalPrinterService.reconnectingUsb' }
    }

    return null
  })

  // Provide a testable reference to the Capacitor App plugin
  private appPlugin: AppPlugin = App

  constructor (
    private config: ConfigService,
    private platform: Platform,
    public swManager: SwManagerService,
    private router: Router,
    public thermalPrinter: ThermalPrinterService,
    private zone: NgZone
  ) {
    this.initializeApp()
  }

  initializeApp () {
    this.platform.ready().then(() => {
      // Hide Splash Screen
      SplashScreen.hide()
      // Update Service Worker
      this.swManager.update()
      // Register device
      this.config.registerDevice()
      // Check deep link
      this.checkDeepLink()
    })
  }

  /**
   * @function checkDeepLink
   * @description Check if exist deeplink
   * @author Iago Rocha
   * @param {void}
   * @returns {boolean}
   */
  checkDeepLink (): boolean {
    try {
      this.appPlugin.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
        this.zone.run(() => {
          // Example url: https://kiosk.nextqs.com/multiple-waiting/objectid
          // slug = /multiple-waiting/objectid
          const strSlug = event.url.split(environment.domain).pop()
          if (strSlug) {
            this.router.navigateByUrl(strSlug)
          }
        })
      })
      return true
    } catch (error) {
      console.error(error)
      return false
    }
  }
}
